import { useEffect, useMemo, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass, Pencil, Trash, X, FloppyDisk, Plus, Hash,
  Buildings, Microphone, Stop, Paperclip, Eye, ArrowsClockwise,
  MagicWand, Sparkle, Spinner, FileText, Image as ImageIcon, CheckCircle, Graph
} from "@phosphor-icons/react";
import LocationPicker from "@/components/LocationPicker";
import IngestionGraph from "@/components/IngestionGraph";

const TYPE_LABELS = {
  LOBBY: "Lobby", SECTOR: "Sector", BLOCK: "Block",
  BUILDING: "Building", FLAT: "Flat",
};

export default function KnowledgeBrowser() {
  const [entries, setEntries] = useState([]);
  const [locs, setLocs] = useState([]);
  const [tags, setTags] = useState([]);
  const [search, setSearch] = useState("");
  const [filterLoc, setFilterLoc] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [mode, setMode] = useState("view"); // view | edit
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [assistBusy, setAssistBusy] = useState(false);
  const [assistDraft, setAssistDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filterLoc) params.location_id = filterLoc;
    if (filterTag) params.hashtag = filterTag;
    const [e, l, t] = await Promise.all([
      api.get("/knowledge", { params }),
      api.get("/locations"),
      api.get("/hashtags"),
    ]);
    setEntries(e.data);
    setLocs(l.data);
    setTags(t.data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterLoc, filterTag]);

  const locById = useMemo(
    () => Object.fromEntries(locs.map((l) => [l.id, l])),
    [locs]
  );

  const pathOf = (id) => {
    const chain = [];
    let cur = locById[id];
    let g = 0;
    while (cur && g < 10) {
      chain.unshift(`${TYPE_LABELS[cur.type]} · ${cur.name}`);
      cur = cur.parent_id ? locById[cur.parent_id] : null;
      g++;
    }
    return chain.join(" / ");
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.title.toLowerCase().includes(q) ||
        e.content.toLowerCase().includes(q) ||
        e.hashtags.some((t) => t.toLowerCase().includes(q))
    );
  }, [entries, search]);

  const open = (e, m = "view") => {
    setOpenId(e.id);
    setMode(m);
    setDraft({ ...e });
  };
  const close = () => {
    setOpenId(null);
    setDraft(null);
    setMode("view");
  };

  const save = async () => {
    if (!draft.title.trim() || !draft.content.trim()) {
      toast.error("Title and content required");
      return;
    }
    if (!draft.location_ids?.length) {
      toast.error("At least one address required");
      return;
    }
    setBusy(true);
    try {
      const cleanAttachments = (draft.attachments || []).map((a) => ({
        name: a.name, mime: a.mime, size: a.size,
        data_url: a.data_url, extracted_text: a.extracted_text || "",
      }));
      const { data } = await api.put(`/knowledge/${draft.id}`, {
        title: draft.title,
        content: draft.content,
        location_ids: draft.location_ids,
        hashtags: draft.hashtags,
        attachments: cleanAttachments,
      });
      toast.success("Updated");
      setEntries((prev) => prev.map((e) => (e.id === data.id ? data : e)));
      setDraft(data);
      setMode("view");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    try {
      await api.delete(`/knowledge/${e.id}`);
      toast.success("Deleted");
      setEntries((prev) => prev.filter((x) => x.id !== e.id));
      if (openId === e.id) close();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const onFile = (ev) => {
    const files = Array.from(ev.target.files || []);
    files.forEach((f) => {
      if (f.size > 2 * 1024 * 1024) {
        toast.error(`${f.name} too large`);
        return;
      }
      const reader = new FileReader();
      reader.onload = async () => {
        const uid = `${f.name}-${f.size}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        const base = { uid, name: f.name, mime: f.type, size: f.size, data_url: reader.result, extracted_text: "", extracting: true };
        setDraft((d) => ({ ...d, attachments: [...(d.attachments || []), base] }));
        try {
          const { data } = await api.post("/extract", {
            data_url: reader.result, mime: f.type, name: f.name,
          });
          setDraft((d) => ({
            ...d,
            attachments: (d.attachments || []).map((x) =>
              x.uid === uid
                ? { ...x, extracted_text: data.extracted_text || "", extract_method: data.method, extract_ms: data.latency_ms, extracting: false }
                : x
            ),
          }));
          if (data.extracted_text) toast.success(`Extracted ${f.name} (${data.method}, ${data.latency_ms}ms)`);
        } catch (err) {
          setDraft((d) => ({
            ...d,
            attachments: (d.attachments || []).map((x) =>
              x.uid === uid ? { ...x, extracting: false, extract_error: true } : x
            ),
          }));
          toast.error(`Extraction failed: ${f.name}`);
        }
      };
      reader.readAsDataURL(f);
    });
    ev.target.value = "";
  };

  // AI writing assist on the draft content
  const runAssist = async () => {
    if (!draft?.content?.trim()) {
      toast.error("Nothing to polish — write something first");
      return;
    }
    setAssistBusy(true);
    setAssistDraft(null);
    try {
      const { data } = await api.post("/assist/expand", {
        text: draft.content,
        location_id: draft.location_ids?.[0] || null,
      });
      setAssistDraft(data);
    } catch (err) {
      toast.error("Assistant unavailable");
    } finally {
      setAssistBusy(false);
    }
  };
  const acceptAssist = () => {
    if (assistDraft) setDraft((d) => ({ ...d, content: assistDraft.rewritten }));
    setAssistDraft(null);
    toast.success("Polished narrative applied");
  };
  const rejectAssist = () => setAssistDraft(null);

  const appendExtractToContent = (att) => {
    if (!att.extracted_text) return;
    setDraft((d) => ({
      ...d,
      content: (d.content ? d.content + "\n\n" : "") + `--- from ${att.name} ---\n${att.extracted_text}`,
    }));
    toast.success(`Pulled ${att.name} into content`);
  };

  // voice in edit
  const recogRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Voice not supported");
    const r = new SR();
    r.continuous = true;
    r.interimResults = false;
    r.lang = "en-IN";
    r.onresult = (ev) => {
      let txt = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        txt += ev.results[i][0].transcript + " ";
      }
      setDraft((d) => ({ ...d, content: (d.content || "") + " " + txt.trim() }));
    };
    r.onend = () => setRecording(false);
    r.onerror = () => setRecording(false);
    r.start();
    recogRef.current = r;
    setRecording(true);
  };
  const stopVoice = () => {
    if (recogRef.current) recogRef.current.stop();
    setRecording(false);
  };

  return (
    <div className="space-y-8" data-testid="knowledge-browser-page">
      <div>
        <div className="mono-label text-orange-600 mb-2">// archive · all addresses</div>
        <h1 className="font-display text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
          Knowledge Archive
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Every fact, every flat. Read, modify, and re-file content stored across the complex.
        </p>
      </div>

      {/* Filter bar */}
      <div className="border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-5 flex items-center gap-2 border border-slate-300 px-3 py-2 focus-within:border-slate-900 transition-all" style={{ borderRadius: 2 }}>
          <MagnifyingGlass size={16} className="text-slate-400" />
          <input
            data-testid="kb-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, content, hashtag…"
            className="flex-1 outline-none text-sm bg-transparent"
          />
        </div>
        <select
          data-testid="kb-filter-location"
          value={filterLoc}
          onChange={(e) => setFilterLoc(e.target.value)}
          className="md:col-span-3 border border-slate-300 px-3 py-2 font-mono text-xs outline-none focus:border-slate-900"
          style={{ borderRadius: 2 }}
        >
          <option value="">All addresses</option>
          {locs.map((l) => (
            <option key={l.id} value={l.id}>
              {TYPE_LABELS[l.type]} · {l.name}
            </option>
          ))}
        </select>
        <select
          data-testid="kb-filter-hashtag"
          value={filterTag}
          onChange={(e) => setFilterTag(e.target.value)}
          className="md:col-span-3 border border-slate-300 px-3 py-2 font-mono text-xs outline-none focus:border-slate-900"
          style={{ borderRadius: 2 }}
        >
          <option value="">All hashtags</option>
          {tags.map((t) => (
            <option key={t.id} value={t.tag_name}>
              #{t.tag_name}
            </option>
          ))}
        </select>
        <button onClick={load} className="btn-sharp btn-secondary text-sm md:col-span-1" data-testid="kb-refresh-btn">
          <ArrowsClockwise size={14} />
        </button>
      </div>

      {/* List */}
      <div className="border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="mono-label">Entries</div>
          <span className="font-mono text-xs px-2 py-0.5 bg-slate-100 border border-slate-200">
            {filtered.length} of {entries.length}
          </span>
        </div>
        {loading ? (
          <div className="p-12 text-center mono-label pulse-soft">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">No entries match these filters</div>
        ) : (
          <div data-testid="kb-list">
            {filtered.map((e) => (
              <div
                key={e.id}
                className="border-b border-slate-100 last:border-b-0 px-5 py-4 hover:bg-slate-50 transition-all flex items-start gap-4"
                data-testid={`kb-row-${e.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-base text-slate-900 font-medium mb-1">{e.title}</div>
                  <div className="text-sm text-slate-600 line-clamp-2 mb-2">
                    {e.content.slice(0, 220)}{e.content.length > 220 ? "…" : ""}
                  </div>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {e.location_ids.map((lid) => (
                      <span key={lid} className="font-mono text-xs px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700">
                        <Buildings size={10} className="inline mr-1" />
                        {pathOf(lid).split(" / ").pop()}
                      </span>
                    ))}
                    {e.hashtags.map((t) => (
                      <span key={t} className="font-mono text-xs px-2 py-0.5 bg-slate-900 text-white">
                        #{t}
                      </span>
                    ))}
                    {(e.attachments || []).length > 0 && (
                      <span className="mono-label text-slate-500">
                        📎 {e.attachments.length}
                      </span>
                    )}
                    {e.ingestion && (
                      <span className="font-mono text-xs px-2 py-0.5 border border-slate-300 bg-white text-slate-700" title={`${e.ingestion.ms}ms`} data-testid={`row-ingest-${e.id}`}>
                        <Graph size={10} className="inline mr-1 text-slate-700" />
                        {e.ingestion.chunks}c · {e.ingestion.entities}e · {e.ingestion.relationships}r
                      </span>
                    )}
                    <span className="mono-label ml-auto">
                      updated {new Date(e.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => open(e, "view")} className="btn-sharp btn-secondary text-xs" data-testid={`kb-view-${e.id}`}>
                    <Eye size={12} /> View
                  </button>
                  <button onClick={() => open(e, "edit")} className="btn-sharp btn-secondary text-xs" data-testid={`kb-edit-${e.id}`}>
                    <Pencil size={12} /> Edit
                  </button>
                  <button onClick={() => remove(e)} className="btn-sharp btn-secondary text-xs text-orange-600 hover:!border-orange-600" data-testid={`kb-delete-${e.id}`}>
                    <Trash size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Drawer / modal */}
      {openId && draft && (
        <div className="fixed inset-0 z-30 bg-slate-900/50 flex justify-end" onClick={close} data-testid="kb-drawer">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl bg-white h-full overflow-y-auto border-l border-slate-200"
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="mono-label text-orange-600">
                  // {mode === "edit" ? "editing entry" : "viewing entry"}
                </div>
                <div className="font-mono text-xs text-slate-500 mt-1">{draft.id}</div>
              </div>
              <div className="flex items-center gap-2">
                {mode === "view" ? (
                  <button onClick={() => setMode("edit")} className="btn-sharp btn-secondary text-sm" data-testid="kb-drawer-edit-btn">
                    <Pencil size={14} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={() => { setDraft(entries.find((x) => x.id === openId)); setMode("view"); }} className="btn-sharp btn-secondary text-sm" data-testid="kb-drawer-cancel-btn">
                      Cancel
                    </button>
                    <button onClick={save} disabled={busy} className="btn-sharp btn-primary text-sm disabled:opacity-50" data-testid="kb-drawer-save-btn">
                      <FloppyDisk size={14} weight="bold" />
                      {busy ? "Saving…" : "Save"}
                    </button>
                  </>
                )}
                <button onClick={close} className="btn-sharp btn-ghost" data-testid="kb-drawer-close-btn">
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <div className="mono-label mb-2">Title</div>
                {mode === "edit" ? (
                  <input
                    data-testid="kb-edit-title"
                    value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    className="w-full border border-slate-300 px-3 py-2 outline-none focus:border-slate-900 text-base"
                    style={{ borderRadius: 2 }}
                  />
                ) : (
                  <div className="font-display text-2xl text-slate-900 leading-snug">{draft.title}</div>
                )}
              </div>

              {/* Addresses */}
              <div>
                <div className="mono-label mb-2">Addresses</div>
                {mode === "edit" ? (
                  <LocationPicker
                    selected={draft.location_ids}
                    onChange={(ids) => setDraft({ ...draft, location_ids: ids })}
                  />
                ) : (
                  <div className="space-y-1">
                    {draft.location_ids.map((lid) => (
                      <div key={lid} className="font-mono text-sm border border-slate-200 px-3 py-2 bg-slate-50">
                        <Buildings size={12} className="inline mr-2 text-slate-500" />
                        {pathOf(lid)}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Hashtags */}
              <div>
                <div className="mono-label mb-2">Hashtags</div>
                {mode === "edit" ? (
                  <div>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {draft.hashtags.map((t) => (
                        <span key={t} className="inline-flex items-center gap-1 bg-slate-900 text-white font-mono text-xs px-2 py-1">
                          <Hash size={10} />
                          {t}
                          <button
                            onClick={() => setDraft({ ...draft, hashtags: draft.hashtags.filter((x) => x !== t) })}
                            className="ml-1"
                            data-testid={`kb-edit-tag-remove-${t}`}
                          >
                            <X size={10} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {tags.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => !draft.hashtags.includes(t.tag_name) && setDraft({ ...draft, hashtags: [...draft.hashtags, t.tag_name] })}
                          className="font-mono text-xs px-2 py-1 border border-slate-200 hover:border-slate-900 transition-all"
                          data-testid={`kb-edit-tag-add-${t.tag_name}`}
                        >
                          + #{t.tag_name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {draft.hashtags.length === 0 ? (
                      <span className="text-slate-400 text-sm">— no tags —</span>
                    ) : draft.hashtags.map((t) => (
                      <span key={t} className="font-mono text-xs px-2 py-1 bg-slate-900 text-white">
                        #{t}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Content */}
              <div>
                <div className="mono-label mb-2 flex items-center justify-between">
                  <span>Content</span>
                  {mode === "edit" && (
                    <div className="flex items-center gap-2">
                      {!recording ? (
                        <button onClick={startVoice} className="btn-sharp btn-secondary text-xs" data-testid="kb-edit-voice-start">
                          <Microphone size={12} /> Voice
                        </button>
                      ) : (
                        <button onClick={stopVoice} className="btn-sharp btn-primary text-xs" data-testid="kb-edit-voice-stop">
                          <Stop size={12} weight="fill" /> Stop
                        </button>
                      )}
                      <button
                        onClick={runAssist}
                        disabled={assistBusy}
                        className="btn-sharp text-xs border bg-slate-900 text-white border-slate-900 hover:bg-slate-800 transition-all disabled:opacity-50 inline-flex items-center gap-2"
                        style={{ borderRadius: 2 }}
                        data-testid="kb-edit-assist-btn"
                      >
                        {assistBusy ? <Spinner size={12} className="animate-spin" /> : <MagicWand size={12} weight="fill" className="text-orange-400" />}
                        {assistBusy ? "Polishing…" : "AI Polish"}
                      </button>
                    </div>
                  )}
                </div>
                {mode === "edit" ? (
                  <textarea
                    data-testid="kb-edit-content"
                    value={draft.content}
                    onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                    rows={14}
                    className="w-full border border-slate-300 px-3 py-2 outline-none focus:border-slate-900 text-sm font-mono leading-relaxed resize-none"
                    style={{ borderRadius: 2 }}
                  />
                ) : (
                  <div className="border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-900 font-mono leading-relaxed whitespace-pre-wrap" data-testid="kb-view-content">
                    {draft.content}
                  </div>
                )}

                {/* assist preview */}
                {mode === "edit" && assistDraft && (
                  <div className="mt-3 border border-slate-900 bg-slate-50" data-testid="kb-assist-preview">
                    <div className="px-3 py-2 bg-slate-900 text-white flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkle size={12} weight="fill" className="text-orange-400" />
                        <span className="mono-label text-slate-300">Polished narrative</span>
                      </div>
                      <span className="font-mono text-xs text-slate-400">
                        {assistDraft.latency_ms}ms · Claude Sonnet 4.5
                      </span>
                    </div>
                    <div className="px-3 py-3 max-h-56 overflow-y-auto text-xs text-slate-900 font-mono leading-relaxed whitespace-pre-wrap">
                      {assistDraft.rewritten}
                    </div>
                    <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-end gap-2">
                      <button onClick={rejectAssist} className="btn-sharp btn-secondary text-xs" data-testid="kb-assist-reject-btn">
                        Discard
                      </button>
                      <button onClick={acceptAssist} className="btn-sharp btn-primary text-xs" data-testid="kb-assist-accept-btn">
                        <CheckCircle size={12} weight="bold" /> Use this version
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Attachments */}
              <div>
                <div className="mono-label mb-2 flex items-center justify-between">
                  <span>Attachments ({(draft.attachments || []).length})</span>
                  {mode === "edit" && (
                    <label className="btn-sharp btn-secondary text-xs cursor-pointer">
                      <Paperclip size={12} /> Add
                      <input type="file" multiple onChange={onFile} className="hidden" data-testid="kb-edit-file-input" accept=".pdf,image/*,.txt,.md,.csv" />
                    </label>
                  )}
                </div>
                {(draft.attachments || []).length === 0 ? (
                  <div className="text-slate-400 text-sm">— no attachments —</div>
                ) : (
                  <div className="space-y-2">
                    {(draft.attachments || []).map((a, i) => {
                      const k = a.uid || `${a.name}-${a.size}-${i}`;
                      return (
                      <div key={k} className="border border-slate-200">
                        <div className="flex items-center justify-between px-3 py-2 text-sm font-mono bg-white">
                          <div className="flex items-center gap-2 min-w-0">
                            {a.mime?.includes("pdf") ? <FileText size={14} className="text-red-600" /> : a.mime?.startsWith("image/") ? <ImageIcon size={14} className="text-blue-600" /> : <Paperclip size={14} className="text-slate-500" />}
                            <a href={a.data_url} download={a.name} className="text-slate-900 hover:text-orange-600 truncate">
                              {a.name}
                            </a>
                            <span className="text-slate-400 shrink-0">· {Math.round(a.size / 1024)}KB</span>
                            {a.extracting && (
                              <span className="mono-label text-orange-600 flex items-center gap-1">
                                <Spinner size={10} className="animate-spin" /> extracting…
                              </span>
                            )}
                            {!a.extracting && a.extracted_text && (
                              <span className="mono-label text-emerald-600">
                                ✓ {a.extract_method || "indexed"} {a.extract_ms ? `· ${a.extract_ms}ms` : ""}
                              </span>
                            )}
                          </div>
                          {mode === "edit" && (
                            <button
                              onClick={() => setDraft({ ...draft, attachments: draft.attachments.filter((_, j) => j !== i) })}
                              className="text-orange-600 ml-2 shrink-0"
                              data-testid={`kb-edit-attach-remove-${i}`}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                        {a.extracted_text && (
                          <div className="border-t border-slate-200 bg-slate-50 px-3 py-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="mono-label">Extracted text · indexed for RAG</span>
                              {mode === "edit" && (
                                <button onClick={() => appendExtractToContent(a)} className="mono-label text-orange-600 hover:underline" data-testid={`kb-append-extract-${k}`}>
                                  ↳ append to content
                                </button>
                              )}
                            </div>
                            <div className="text-xs text-slate-700 font-mono leading-relaxed whitespace-pre-wrap max-h-40 overflow-y-auto">
                              {a.extracted_text}
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Ingestion Graph */}
              <div className="pt-4 border-t border-slate-100">
                <IngestionGraph
                  knowledgeId={draft.id}
                  ingestion={draft.ingestion}
                  onReingested={(d) => {
                    const updated = { ...draft, ingestion: { ...d } };
                    setDraft(updated);
                    setEntries((prev) => prev.map((x) => (x.id === draft.id ? { ...x, ingestion: { ...d } } : x)));
                  }}
                />
              </div>

              {/* Meta */}
              <div className="pt-4 border-t border-slate-100 grid grid-cols-2 gap-4 mono-label">
                <div>
                  Created<br />
                  <span className="text-slate-700 font-mono text-xs">
                    {new Date(draft.created_at).toLocaleString()}
                  </span>
                </div>
                <div>
                  Updated<br />
                  <span className="text-slate-700 font-mono text-xs">
                    {new Date(draft.updated_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
